import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreateQuiz, useUpdateQuiz } from '@/hooks/useQuizzes';
import type { Quiz } from '@/types/quiz';

interface QuizFormDialogProps {
  open: boolean;
  onClose: () => void;
  quiz?: Quiz;
}

export function QuizFormDialog({ open, onClose, quiz }: QuizFormDialogProps) {
  const isEdit = !!quiz;
  const createMutation = useCreateQuiz();
  const updateMutation = useUpdateQuiz();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [slug, setSlug] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setError('');
      if (quiz) {
        setTitle(quiz.title);
        setDescription(quiz.description || '');
        setSlug('');
      } else {
        setTitle('');
        setDescription('');
        setSlug('');
      }
    }
  }, [open, quiz]);

  if (!open) return null;

  const isPending = createMutation.isPending || updateMutation.isPending;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError('Quiz-Titel ist ein Pflichtfeld.');
      return;
    }

    try {
      if (isEdit && quiz) {
        await updateMutation.mutateAsync({
          quizId: quiz.quizId,
          data: {
            title: trimmedTitle,
            description: description.trim() || null,
            ...(slug.trim() && { slug: slug.trim() }),
          },
        });
      } else {
        await createMutation.mutateAsync({
          title: trimmedTitle,
          ...(description.trim() && { description: description.trim() }),
          ...(slug.trim() && { slug: slug.trim() }),
        });
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten.');
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onClose}
        onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
        role="button"
        tabIndex={-1}
        aria-label="Dialog schließen"
      />
      <div className="relative z-50 w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg mx-4">
        <h2 className="text-lg font-semibold">
          {isEdit ? 'Quiz bearbeiten' : 'Neues Quiz erstellen'}
        </h2>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="quiz-title">Quiz-Titel *</Label>
            <Input
              id="quiz-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="z.B. Hautpflege-Berater"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="quiz-description">Beschreibung</Label>
            <textarea
              id="quiz-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Kurze Beschreibung des Quiz-Zwecks"
              rows={3}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="quiz-slug">URL-Slug</Label>
            <Input
              id="quiz-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="Auto-generiert aus Titel"
            />
          </div>

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Wird gespeichert...' : isEdit ? 'Speichern' : 'Quiz erstellen'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
